import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import { DocumentsDropdown } from './DocumentsDropdown';

interface NavigationItem {
  id: string;
  label: string;
  url: string;
  icon?: string;
  is_dropdown: boolean;
  is_external: boolean;
  parent_id?: string | null;
  order: number;
}

interface NavigationSubItem {
  id: string;
  label: string;
  url: string;
  parent_id: string;
  is_external: boolean;
}

interface DocumentCategory {
  name: string;
  slug: string;
  icon: string;
  subcategories: {
    name: string;
    slug: string;
  }[];
}

interface NavigationMenuProps {
  navItems: NavigationItem[];
  navSubItems: NavigationSubItem[];
  categories: DocumentCategory[];
  loading: boolean;
}

export function NavigationMenu({ navItems, navSubItems, categories, loading }: NavigationMenuProps) {
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);

  const handleDropdownClick = (slug: string, event?: React.MouseEvent) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    setOpenDropdown(openDropdown === slug ? null : slug);
  };

  const closeDropdowns = () => {
    setOpenDropdown(null);
  };

  // Get subitems for a specific parent item
  const getSubItems = (parentId: string) => {
    return navSubItems.filter(item => item.parent_id === parentId);
  };

  // Handle submenu item click to prevent dropdown closing before navigation
  const handleSubmenuItemClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    closeDropdowns();
  };

  return (
    <nav className="flex space-x-4 lg:space-x-8">
      {/* Documents Dropdown */}
      {loading ? (
        <div className="h-6 w-24 bg-gray-200 dark:bg-gray-700 animate-pulse rounded"></div>
      ) : (
        <DocumentsDropdown 
          categories={categories}
          isOpen={openDropdown === 'documents'}
          onToggle={(e) => handleDropdownClick('documents', e)}
          onClose={closeDropdowns}
        />
      )}

      {/* Main Navigation Links from Database */}
      {navItems.map((link) => (
        link.is_dropdown ? (
          // Custom Dropdown Menu
          <div key={link.id} className="relative group">
            <div 
              onClick={(e) => handleDropdownClick(link.id as string, e)}
              className="flex items-center space-x-1 text-gray-700 dark:text-gray-100 hover:text-primary-600 dark:hover:text-primary-400 transition-colors focus:outline-none text-sm lg:text-base cursor-pointer"
            >
              <span>{link.label}</span>
              <ChevronDown
                size={16}
                className={`transform transition-transform duration-300 ${
                  openDropdown === link.id ? 'rotate-180' : ''
                }`}
              />
            </div>
            
            <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-primary-600 dark:bg-primary-400 transition-all duration-300 group-hover:w-full"></span>
            
            {openDropdown === link.id && (
              <div className="absolute left-0 mt-2 w-64 bg-white dark:bg-gray-800 rounded-lg shadow-lg py-2 animate-fade-in z-[60]">
                {getSubItems(link.id as string).map((subItem) => (
                  <Link
                    key={subItem.id}
                    to={subItem.url}
                    className="block px-4 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-100 hover:text-primary-600 dark:hover:text-primary-400"
                    onClick={handleSubmenuItemClick}
                    target={subItem.is_external ? "_blank" : undefined}
                    rel={subItem.is_external ? "noopener noreferrer" : undefined}
                  >
                    {subItem.label}
                  </Link>
                ))}
              </div>
            )}
          </div>
        ) : (
          // Regular Link
          <Link
            key={link.id}
            to={link.url}
            className="text-gray-700 dark:text-gray-100 hover:text-primary-600 dark:hover:text-primary-400 transition-colors relative group text-sm lg:text-base"
            target={link.is_external ? "_blank" : undefined}
            rel={link.is_external ? "noopener noreferrer" : undefined}
          >
            {link.label}
            <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-primary-600 dark:bg-primary-400 transition-all duration-300 group-hover:w-full"></span>
          </Link>
        )
      ))}

      {/* Fallback Navigation if no items from database */}
      {navItems.length === 0 && [
        { text: 'Serviços', path: '/servicos' },
        { text: 'Notícias', path: '/noticias' },
        { text: 'Licitações', path: '/licitacoes' },
        { text: 'Concursos', path: '/concursos' },
        { text: 'Contato', path: '/contato' },
      ].map((link) => (
        <Link
          key={link.path}
          to={link.path}
          className="text-gray-700 dark:text-gray-100 hover:text-primary-600 dark:hover:text-primary-400 transition-colors relative group text-sm lg:text-base"
        >
          {link.text}
          <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-primary-600 dark:bg-primary-400 transition-all duration-300 group-hover:w-full"></span>
        </Link>
      ))}
    </nav>
  );
}